import React, { useState, useEffect } from "react";
import { Tabs, Tab, Paper } from "@material-ui/core";
import { Club } from "api/clubs";
import { getClub } from "api/admin/clubs";
import { getCompanySubscriptons, Subscription } from "api/company";
import { HelpscoutFab } from "components";
import { Membership } from "api/memberships";
import { View } from "features/global/reducer";
import { useSnackbar } from "notistack";
import i18n from "./i18n";
import MemberSettingsTab from "./MemberSettingsTab";
import CompanySettingsTab from "./CompanySettingsTab";
import ProfileTab from "./ProfileTab";
import DomainAssociationsTab from "./DomainAssociationsTab";
import "./b2bfeaturesStyle.scss";

// prop types
interface B2BFeaturesViewProps {
    club: Club | null,
    memberships: Membership[],
    onNavigateTo: ({ view, tab }: { view: View, tab?: number }) => void,
}

// club-related admin stuff
const B2BFeaturesView = ({ club: currentClub, memberships, onNavigateTo }: B2BFeaturesViewProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );
    // ui state
    const [club, setClub] = useState<Club>();
    const [currentTab, setCurrentTab] = useState(0);
    const [subscription, setSubscription] = useState<Subscription | null>(null);

    // reset ui
    const fetchClub = async (clubId: string) => setClub(await getClub({ clubId }));
    const getSubscriptions = async (clubId: string) => {
        await getSubscriptionDetail(currentClub!.id).then((data) => {
            setSubscription(data);
        });
    };
    useEffect(() => {
        if (currentClub) {
            fetchClub(currentClub!.id);
            getSubscriptions(currentClub!.id);
        }
    }, [currentClub]);

    const getSubscriptionDetail = async (clubId: string): Promise<Subscription | null> => {
        try {
            const subscriptions: Subscription[] = await getCompanySubscriptons({ clubId });
            if (subscriptions.length === 0) return null;
            return subscriptions[0];
        } catch (error) {
            displayError(error);
            return null;
        }
    };

    const userRole = memberships.find((m) => m.club.id === club?.id)?.userRole;
    const isAdminOrOwner = (userRole === "admin" || userRole === "owner");
    const isCompany = club?.capabilities.includes("company");
    return (
        <>
            <HelpscoutFab club={club} />
            <Paper style={{ display: "flex", justifyContent: "space-between" }}>
                {/* tabs */}
                <Tabs
                    indicatorColor="primary"
                    textColor="primary"
                    value={currentTab}
                    onChange={(e, index) => setCurrentTab(index)}
                >
                    <Tab className="b2btab" label={i18n.generalSettings} value={0} />
                    <Tab className="b2btab" label={i18n.memberSettings} value={1} />
                    {isAdminOrOwner && isCompany && <Tab className="b2btab" label={i18n.domains} value={2} />}
                    {isAdminOrOwner && isCompany && <Tab className="b2btab" label={i18n.profile} value={3} />}
                </Tabs>
            </Paper>
            {currentTab === 0 && club && <CompanySettingsTab subscription={subscription} club={club} onNavigateTo={onNavigateTo} />}
            {currentTab === 1 && club && <MemberSettingsTab subscription={subscription} club={club} onNavigateTo={onNavigateTo} />}
            {currentTab === 2 && club && <DomainAssociationsTab subscription={subscription} club={club} onNavigateTo={onNavigateTo} />}
            {currentTab === 3 && club && (<ProfileTab subscription={subscription} club={club} onNavigateTo={onNavigateTo} />)}
        </>
    );
};

export default B2BFeaturesView;
